import { queryKeys, useHttpClient, useSession } from '@repo/api-client/react';
import {
  OrganizationSchema,
  type CreateOrganizationRequest,
  type Organization,
  type OrganizationListItem,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';
import { syncOrgsFromCache } from './sessionSync';

/**
 * Create a new organization. Mirrors the `POST /v1/orgs` route which
 * also creates an `owner` membership for the caller.
 *
 * On success we optimistically prepend the new org onto the cached
 * list (with `role: 'owner'`) so screens that already rendered
 * `useOrganizations()` reflect the change without a network round-
 * trip; the list is invalidated as well so the next read still
 * reconciles with the server.
 */
export function useCreateOrganization() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const { syncOrganizations } = useSession();

  const mutation = useMutation({
    mutationFn: (body: CreateOrganizationRequest) =>
      postJSON(http, '/orgs', body, OrganizationSchema),
    onSuccess: (organization) => {
      const listItem: OrganizationListItem = {
        ...organization,
        role: 'owner',
      };
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) =>
          cache ? { ...cache, items: [...cache.items, listItem] } : { items: [listItem] },
      );
      queryClient.setQueryData<Organization>(
        queryKeys.organizations.detail(organization.id),
        organization,
      );
      // Mirror into the session projection BEFORE we let `mutateAsync`
      // resolve. The caller typically chases this with
      // `setActiveOrganization(created.id)`, and the active-org guard
      // would snap back to `organizations[0]` if it ran against a
      // session list that didn't include the new row yet.
      syncOrgsFromCache(queryClient, syncOrganizations);
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all() });
    },
  });

  return {
    createOrganization: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
